import React from 'react'

import { Avatar, Tooltip } from 'antd'
import styled from 'styled-components'

import { SegmentSpan, SText } from '../styled'
import { Segment, User } from '../types'

interface MentionSegmentProps {
  segment: Segment
  user: User | undefined
  isNavigating: boolean
  isActive: boolean
  onClick: (e: React.MouseEvent<HTMLSpanElement>) => void
  onKeyDown: (e: React.KeyboardEvent<HTMLSpanElement>) => void
  onInput: (e: React.FormEvent<HTMLSpanElement>) => void
  onPaste: (e: React.ClipboardEvent<HTMLSpanElement>) => void
}

export const MentionSegment = React.forwardRef<
  HTMLSpanElement,
  MentionSegmentProps
>(
  (
    {
      segment,
      user,
      isNavigating,
      isActive,
      onClick,
      onKeyDown,
      onInput,
      onPaste,
    },
    ref,
  ) => {
    const segmentSpan = (
      <SegmentSpan
        ref={ref}
        data-segment-id={segment.id}
        contentEditable={!isNavigating ? isActive : true}
        onClick={onClick}
        onKeyDown={onKeyDown}
        onInput={onInput}
        onPaste={onPaste}
        suppressContentEditableWarning={true}
        type={segment.type}
      >
        {segment.content}
      </SegmentSpan>
    )

    // Default to 'mention' type behavior
    const tooltipContent = user ? (
      <STooltipContent>
        <SAvatar
          $hasAvatar={!!user.avatarUrl}
          src={user.avatarUrl || undefined}
        >
          {user.name?.[0] || '?'}
        </SAvatar>

        <TooltipText>
          <SText>
            {user.name}
          </SText>

          {(user as any).phone_or_email && (
            <EmailText>
              {(user as any).phone_or_email}
            </EmailText>
          )}
        </TooltipText>
      </STooltipContent>
    ) : (
      'User not found'
    )

    if (segment.isModified) {
      return segmentSpan
    }

    return (
      <Tooltip
        color='var(--color-dark-bg)'
        title={tooltipContent}
        mouseEnterDelay={0.5}
      >
        {segmentSpan}
      </Tooltip>
    )
  },
)

const EmailText = styled.p`
  font-size: 12px;
  color: rgba(255, 255, 255, 0.45);
`
const TooltipText = styled.div`
  display: flex;
  overflow: hidden;
  flex-direction: column;
`

const STooltipContent = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 4px;
`

const SAvatar = styled(Avatar)<{ $hasAvatar?: boolean }>`
  min-width: 32px;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background-color: ${props => props.$hasAvatar ? 'transparent' : '#00000040'};
  font-size: 14px;
  color: #FFFFFF;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  text-transform: uppercase;
  position: relative;
`

MentionSegment.displayName = 'MentionSegment'
